import React, { useMemo } from 'react';
import type { OperationStep } from '@/types/operations';
import { pageStyles } from '@/components/utils/PageLayout';
import { Placeholder } from '@/components/ui';
import { formatCategoryLabel } from './presentation';
import StepRow from './StepRow';
import styles from './OperationsChecklist.module.css';

interface StepListProps {
  steps: OperationStep[];
  readOnly: boolean;
  busyStepId: number | null;
  onRefresh: (step: OperationStep) => void;
  onConfirm: (step: OperationStep) => void;
  onExempt: (step: OperationStep) => void;
}

// Steps arrive ordered by sort_order; grouping preserves that order.
// Category headers appear in the order their first step does.
const groupByCategory = (steps: OperationStep[]): [string, OperationStep[]][] => {
  const groups = new Map<string, OperationStep[]>();
  for (const step of steps) {
    const existing = groups.get(step.category);
    if (existing) existing.push(step);
    else groups.set(step.category, [step]);
  }
  return Array.from(groups.entries());
};

const StepList: React.FC<StepListProps> = ({
  steps,
  readOnly,
  busyStepId,
  onRefresh,
  onConfirm,
  onExempt,
}) => {
  const groups = useMemo(() => groupByCategory(steps), [steps]);

  return (
    <div className={pageStyles['table-wrapper']}>
      <div className={pageStyles['table-scroll']}>
        <table className={pageStyles.table}>
          <thead>
            <tr>
              <th>Step</th>
              <th>Type</th>
              <th>Status</th>
              <th>Summary</th>
              <th>Last Run</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {steps.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <Placeholder message="No steps in this checklist." />
                </td>
              </tr>
            ) : (
              groups.map(([category, categorySteps]) => (
                <React.Fragment key={category}>
                  <tr className={styles['category-row']}>
                    <td colSpan={6}>{formatCategoryLabel(category)}</td>
                  </tr>
                  {categorySteps.map((step) => (
                    <StepRow
                      key={step.id}
                      step={step}
                      readOnly={readOnly}
                      busy={busyStepId === step.id}
                      onRefresh={onRefresh}
                      onConfirm={onConfirm}
                      onExempt={onExempt}
                    />
                  ))}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StepList;
